import type { ButtonProps, UnstyledButtonProps, FloatingPosition } from '@mantine/core';
import {
  Button,
  Text,
  Group,
  Stack,
  Badge,
  createPolymorphicComponent,
  UnstyledButton,
} from '@mantine/core';
import { HoverCard } from '~/components/MantineOverlays';
import { useRouter } from 'next/router';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import type { IconProps, Icon } from '@tabler/icons-react';
import {
  IconSparkles,
  IconHeart,
  IconGift,
  IconRocket,
  IconBolt,
  IconStar,
  IconTrophy,
  IconCrown,
  IconDiamond,
} from '@tabler/icons-react';
import { useAppContext } from '~/providers/AppProvider';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { Random } from '~/utils/random';
import { isHolidaysTime } from '~/utils/date-helpers';
import classes from './SupportButton.module.scss';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import React, { forwardRef } from 'react';
import clsx from 'clsx';

type Variant = 'primary' | 'gift' | 'heart' | 'sparkle' | 'royal' | 'premium';
type SupportButtonOption = {
  text: string;
  icon: ForwardRefExoticComponent<IconProps & React.RefAttributes<Icon>>;
  variant: Variant;
  href: string;
};

const options: SupportButtonOption[] = [
  {
    text: 'Pro',
    icon: IconDiamond,
    variant: 'royal',
    href: '/pricing?utm_campaign=support_pro',
  },
  // {
  //   text: 'Upgrade',
  //   icon: IconRocket,
  //   variant: 'primary',
  //   href: '/pricing?utm_campaign=support_upgrade',
  // },
  // {
  //   text: 'Premium',
  //   icon: IconDiamond,
  //   variant: 'premium',
  //   href: '/pricing?utm_campaign=support_premium',
  // },
  // {
  //   text: 'Elite',
  //   icon: IconTrophy,
  //   variant: 'sparkle',
  //   href: '/pricing?utm_campaign=support_elite',
  // },
  // {
  //   text: 'VIP',
  //   icon: IconBolt,
  //   variant: 'sparkle',
  //   href: '/pricing?utm_campaign=support_vip',
  // },
  // {
  //   text: 'Perks',
  //   icon: IconStar,
  //   variant: 'heart',
  //   href: '/pricing?utm_campaign=support_perks',
  // },
];

const holidayButton: SupportButtonOption = {
  text: 'Holiday',
  icon: IconGift,
  variant: 'gift',
  href: '/pricing?utm_campaign=holiday_promo',
};

export function useSupportOption() {
  const { seed } = useAppContext();
  const features = useFeatureFlags();
  const selectedOption = new Random(seed).fromArray(options);
  // On non-green envs /pricing just shows a "memberships unavailable" page —
  // funnel users to the buzz purchase flow where NoCryptoUpsell pitches the
  // membership in context.
  const href = features.isGreen ? selectedOption.href : '/purchase/buzz';

  return { ...selectedOption, href };
}

export const SupportButton = () => {
  const selectedOption = useSupportOption();
  const { href } = selectedOption;

  return (
    <HoverCard withArrow openDelay={400} closeDelay={100}>
      <HoverCard.Target>
        <SupportButtonPolymorphic
          component={Link}
          href={href}
          className={`${classes.supportButtonMediaQuery}`}
          classVariant={selectedOption.variant}
          variant="filled"
          px="xs"
          icon={selectedOption.icon}
        >
          {selectedOption.text}
        </SupportButtonPolymorphic>
      </HoverCard.Target>
      <HoverCard.Dropdown className={classes.supportHoverCard}>
        <Stack gap="xs">
          <Group gap="xs">
            <IconSparkles size={18} color="var(--mantine-color-yellow-6)" />
            <Text fw={600} size="sm" c="yellow.6">
              Unlock Premium Benefits!
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Join thousands of creators with exclusive perks, priority support, and advanced
            features.
          </Text>
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

function getVariantStyles(variant: Variant) {
  switch (variant) {
    case 'primary':
      return classes.supportButtonPrimary;
    case 'gift':
      return classes.supportButtonGift;
    case 'heart':
      return classes.supportButtonHeart;
    case 'sparkle':
      return classes.supportButtonSparkle;
    case 'royal':
      return classes.supportButtonRoyal;
    case 'premium':
      return classes.supportButtonPremium;
    default:
      return classes.supportButtonPrimary;
  }
}

interface SupportButtonBaseProps
  extends UnstyledButtonProps,
    Omit<React.ComponentPropsWithoutRef<'button'>, keyof UnstyledButtonProps> {
  icon?: ForwardRefExoticComponent<IconProps & React.RefAttributes<Icon>>;
  position?: FloatingPosition;
  classVariant?: Variant;
}

const SupportButtonBase = forwardRef<HTMLButtonElement, SupportButtonBaseProps>(
  ({ position, icon: Icon, children, className, classVariant = 'primary', ...props }, ref) => {
    return (
      <HoverCard withArrow openDelay={400} closeDelay={100} position={position} withinPortal>
        <HoverCard.Target>
          <UnstyledButton
            ref={ref}
            className={clsx(
              `${classes.supportButton} ${getVariantStyles(classVariant)}`,
              className
            )}
            {...props}
          >
            {children && (
              <Text component="span" size="xs" fw={700} className={classes.supportButtonText}>
                {children}
              </Text>
            )}
            {Icon && <Icon size={16} className={classes.supportButtonIcon} />}
          </UnstyledButton>
        </HoverCard.Target>
        <HoverCard.Dropdown className={classes.supportHoverCard}>
          <Stack gap="xs">
            <Group gap="xs">
              <IconSparkles size={18} color="var(--mantine-color-yellow-6)" />
              <Text fw={600} size="sm" c="yellow.6">
                Unlock Premium Benefits!
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              Join thousands of creators with exclusive perks, priority support, and advanced
              features.
            </Text>
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    );
  }
);
SupportButtonBase.displayName = 'SupportButtonBase';
export const SupportButtonPolymorphic = createPolymorphicComponent<
  'button',
  SupportButtonBaseProps
>(SupportButtonBase);

/**
 * The membership offer as a pill small enough to sit under a name. A span with a link role, not an
 * anchor: the user menu renders it inside the profile link.
 */
export function SupportChip({ onClick }: { onClick?: () => void }) {
  const { href, icon: Icon, variant } = useSupportOption();
  const router = useRouter();
  const open = (event: React.SyntheticEvent, newTab = false) => {
    event.preventDefault();
    event.stopPropagation();
    onClick?.();
    if (newTab) window.open(href, '_blank', 'noopener');
    else void router.push(href);
  };
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={(event) => open(event, event.metaKey || event.ctrlKey)}
      onAuxClick={(event) => event.button === 1 && open(event, true)}
      onKeyDown={(event) => event.key === 'Enter' && open(event, event.metaKey || event.ctrlKey)}
      className={clsx(classes.supportButton, classes.supportChip, getVariantStyles(variant))}
    >
      <Text component="span" size="xs" fw={700} className={classes.supportButtonText}>
        Become a member
      </Text>
      <Icon size={14} className={classes.supportButtonIcon} />
    </span>
  );
}
